import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Radix dropdown menu. The standard home for table row-action overflow —
 * row actions are always visible or behind this menu, never revealed by
 * hover-opacity (DESIGN.md §6).
 */
interface MenuProps {
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  readonly align?: 'start' | 'end';
  readonly testId?: string;
}

export function DropdownMenu({ trigger, children, align = 'end', testId }: MenuProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>{trigger}</DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align}
          sideOffset={4}
          data-testid={testId}
          className="z-50 min-w-[180px] rounded-lg border border-line bg-raised p-1 shadow-overlay animate-panel-in"
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

interface ItemProps {
  readonly children: ReactNode;
  readonly onSelect: () => void;
  readonly tone?: 'default' | 'danger';
  readonly disabled?: boolean;
  readonly testId?: string;
}

export function DropdownMenuItem({ children, onSelect, tone = 'default', disabled = false, testId }: ItemProps): JSX.Element {
  return (
    <DropdownMenuPrimitive.Item
      onSelect={onSelect}
      disabled={disabled}
      data-testid={testId}
      className={cx(
        'flex cursor-default select-none items-center gap-2 rounded px-2.5 py-1.5 text-dense outline-none transition-colors duration-120 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        tone === 'danger'
          ? 'text-bad data-[highlighted]:bg-bad-dim'
          : 'text-paper-mid data-[highlighted]:bg-active data-[highlighted]:text-paper',
      )}
    >
      {children}
    </DropdownMenuPrimitive.Item>
  );
}

export function DropdownMenuSeparator(): JSX.Element {
  return <DropdownMenuPrimitive.Separator className="mx-1 my-1 h-px bg-line-soft" />;
}
